import {
  ChangeDetectorRef,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  signal,
  TemplateRef,
  viewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateDirective } from '@ngx-translate/core';
import { EMPTY, Observable } from 'rxjs';
import {
  ButtonComponent,
  CurrencyPipe,
  HoldingResponse,
  IfFeatureService,
  HrefDirective,
  ListComponent,
  ListItemComponent,
  ListItemLabelComponent,
  ListItemValueComponent,
  ModalRef,
  ModalService,
  OverlayAsyncOpenEvent,
  OverlayComponent,
  OverlayHeaderComponent,
  OverlayService,
  OverlayToggleDirective,
  PercentPipe,
  SymbolLogoComponent,
  TickerSymbolComponent,
  TickerSymbolTableContainerComponent,
  DataAnalyticsService,
  LinkAccessibilityDirective,
  NotificationComponent,
  HelpLinkDirective,
  SecurityService
} from '@tdwb/core';
import { FplRecallSecurityComponent } from '../recall-security/fpl-recall-security.component';
import { FplRestService } from '../services/fpl-rest.service';
import { StockLoanDetails } from '../fpl.domain';

@Component({
  selector: 'td-wb-fpl-on-loan',
  templateUrl: 'fpl-on-loan.component.html',
  styleUrls: ['fpl-on-loan.component.scss'],
  imports: [
    OverlayToggleDirective,
    OverlayComponent,
    OverlayHeaderComponent,
    SymbolLogoComponent,
    ListComponent,
    ListItemComponent,
    ListItemLabelComponent,
    ListItemValueComponent,
    ButtonComponent,
    HrefDirective,
    FplRecallSecurityComponent,
    TickerSymbolComponent,
    TickerSymbolTableContainerComponent,
    TranslateDirective,
    CurrencyPipe,
    LinkAccessibilityDirective,
    NotificationComponent,
    HelpLinkDirective
  ]
})
export class FplOnLoanComponent {
  private static readonly DEFAULT_EMPTY_PLACEHOLDER: string = '-';
  private readonly modalService = inject(ModalService);
  private readonly overlayService = inject(OverlayService);
  private readonly fplRestService = inject(FplRestService);
  private readonly percentPipe = inject(PercentPipe);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);
  private readonly ifFeatureService = inject(IfFeatureService);
  private readonly dataAnalyticsService = inject(DataAnalyticsService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly securityService: SecurityService = inject(SecurityService);

  private fplRecallSecurityModalRef: ModalRef<unknown>;
  private asyncOpenEvent: OverlayAsyncOpenEvent;
  protected readonly isRecallActionPending = signal(false);
  protected readonly loanQuantityDisplayValue = computed((): string => {
    const holding = this.holding();
    if (holding === undefined || holding.quantity <= 0) {
      return FplOnLoanComponent.DEFAULT_EMPTY_PLACEHOLDER;
    }

    const loanQuantity = holding.onLoanQuantity ?? 0;
    return `${loanQuantity}/${holding.quantity} (${this.getFormattedInterestRate((loanQuantity / holding.quantity) * 100)})`;
  });

  protected readonly initialInterestRateDisplayValue = signal(FplOnLoanComponent.DEFAULT_EMPTY_PLACEHOLDER);
  protected readonly currentInterestRateDisplayValue = signal(FplOnLoanComponent.DEFAULT_EMPTY_PLACEHOLDER);
  protected readonly showLogo = this.ifFeatureService.getFeatureFlag('wbSymbolLogoOnHoldingsEnabled');
  protected readonly showDividendNotification = this.securityService.hasPermission('DIVIDEND_REINVESTMENT_PLAN');

  readonly accountId = input<string>();
  readonly holding = input.required<HoldingResponse>();
  protected readonly fplRecallSecurityModalContent = viewChild.required<TemplateRef<FplRecallSecurityComponent>>('fplRecallSecurityModalTemplate');

  constructor() {}

  asyncOpen(event: OverlayAsyncOpenEvent): void {
    this.asyncOpenEvent = event;
  }

  openOnLoanOverlay(): void {
    this.dataAnalyticsService.emitLinkClickEvent('stocklending:on loan');
    const holding = this.holding();
    const accountId = this.accountId();
    if (holding.cusip && accountId) {
      this.fplRestService.getStockLoanDetails(holding.cusip, accountId)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (stockLoanDetails: StockLoanDetails): void => {
            if (stockLoanDetails !== undefined) {
              this.isRecallActionPending.set(stockLoanDetails.pendingRecall === true);
              this.populateInterestRates(stockLoanDetails);
              this.openAsyncOnLoanOverlay();
              this.changeDetectorRef.markForCheck();
            }
          },
          error: (): Observable<never> => EMPTY
        });
    }
  }

  private openAsyncOnLoanOverlay(): void {
    if (this.asyncOpenEvent !== undefined) {
      this.asyncOpenEvent.open();
    }
  }

  private populateInterestRates(stockLoanDetails: StockLoanDetails): void {
    // Initial Interest Rate
    if (stockLoanDetails.initialRateMin !== undefined && stockLoanDetails.initialRateMax !== undefined) {
      this.initialInterestRateDisplayValue.set(`${this.getFormattedInterestRate(stockLoanDetails.initialRateMin)} - ${this.getFormattedInterestRate(stockLoanDetails.initialRateMax)}`);
    } else if (stockLoanDetails.initialRateMin !== undefined) {
      this.initialInterestRateDisplayValue.set(this.getFormattedInterestRate(stockLoanDetails.initialRateMin));
    } else if (stockLoanDetails.initialRateMax !== undefined) {
      this.initialInterestRateDisplayValue.set(this.getFormattedInterestRate(stockLoanDetails.initialRateMax));
    }

    // Curent Interest Rate
    if (stockLoanDetails.currentRateMin !== undefined && stockLoanDetails.currentRateMax !== undefined) {
      this.currentInterestRateDisplayValue.set(`${this.getFormattedInterestRate(stockLoanDetails.currentRateMin)} - ${this.getFormattedInterestRate(stockLoanDetails.currentRateMax)}`);
    } else if (stockLoanDetails.currentRateMin !== undefined) {
      this.currentInterestRateDisplayValue.set(this.getFormattedInterestRate(stockLoanDetails.currentRateMin));
    } else if (stockLoanDetails.currentRateMax !== undefined) {
      this.currentInterestRateDisplayValue.set(this.getFormattedInterestRate(stockLoanDetails.currentRateMax));
    }
  }

  private getFormattedInterestRate(interestRate: number): string {
    return this.percentPipe.transform(interestRate, { format: 'n2', emptyPlaceholder: FplOnLoanComponent.DEFAULT_EMPTY_PLACEHOLDER });
  }

  openFplRecallSecurityModal(): void {
    this.overlayService.close();
    this.dataAnalyticsService.emitLinkClickEvent('stocklending:recall');
    this.fplRecallSecurityModalRef = this.modalService.open(this.fplRecallSecurityModalContent(), {
      size: 'extra-small'
    });

    this.fplRecallSecurityModalRef.afterClose().subscribe((result: unknown): void => {
      if (result !== undefined) {
        this.openAsyncOnLoanOverlay();
      }
    });
  }

  closeFplRecallSecurityModal(): void {
    this.fplRecallSecurityModalRef?.close();
  }

  closeOnLoanOverlay(): void {
    this.dataAnalyticsService.emitLinkClickEvent('stocklending:close');
  }
}
